import _ from "lodash";
import md5 from "md5";
import { formatCurrency, format } from "lib/globalize-util";
import type { IScope } from "reducers/scope";
import type { IReviewTabStateAction } from "actions/working-order-state";
import type { IOrderSplitRates, IShippingRate, IShippingRatesResponseEntry } from "types/shipping";
import type { IStore } from "reducers";

export const getSplitKeyLabels = (ratesBySplit: IShippingRatesResponseEntry[], scope: IScope) => {
    const splitKeys = _.map(scope.brand.settings.order_split_fields, (key: string) => key.replace(/_/g, " "));
    return _.map(ratesBySplit, (orderSplit) => {
        const splitKeyValues = orderSplit.split_key.split("_").splice(0, 2); // split by door happens first
        const index = _.indexOf(splitKeys, "warehouse");
        if (index !== -1) {
            splitKeyValues[index] = _.find(scope.brand.settings.warehouses, { _id: splitKeyValues[index] })?.name || "";
        }
        return { ...orderSplit, splitKeys, splitKeyValues };
    });
};
export const formatRatesStatus = (
    orderSplitRates: IShippingRatesResponseEntry,
    { order }: IReviewTabStateAction["payload"],
    scope: IScope,
    meta: IReviewTabStateAction["meta"]
): IOrderSplitRates => {
    const rates: IShippingRate[] =
        // Bad nested props naming
        // TODO: rename 'rates.rates' on backend side
        _.map(_.reject(orderSplitRates.rates.rates, { validation_status: "invalid" }), (rate) => {
            const formattedPrice = formatCurrency(+rate.final_amount, order.currency_code);
            const originalPrice = formatCurrency(+rate.original_amount, order.currency_code);

            // "Default" rate which allows brand to enter custom shipping cost/label
            let rateLabel =
                rate.service_code === "nuorder_generic_default"
                    ? rate.service_type
                    : `${rate.service_type} (${rate.rate_type} ${rate.package_type || ""}) - ${formattedPrice}`;

            if (rate.applied_promotion) {
                rateLabel = `${rate.applied_promotion.name} -- ${rateLabel}`;

                if (rate.applied_promotion.replacement_code) {
                    rateLabel = `${rateLabel} ${format("(originally: |originalPrice|)", { originalPrice })}`;
                }
            }
            return {
                ...rate,
                label: meta.isDropShip ? `${format("Drop Ship")}: ${rateLabel}` : rateLabel,
                formattedPrice,
                is_drop_ship_rate: meta.isDropShip,
            };
        });
    let errors = orderSplitRates.rates.errors;
    if (_.isEmpty(rates) && _.isEmpty(errors)) {
        errors = [{ message: format("Invalid destination address") }];
    }
    if (
        !meta.isDropShip &&
        meta.hasDropShipCarriers &&
        scope.brand.hasPreference("drop_shipping_enabled") &&
        _.isEmpty(errors)
    ) {
        rates.push({
            label: format("Drop Ship"),
            is_drop_ship_rate: true,
            service_code: "",
            package_type: "",
            carrier_id: "",
            final_amount: 0,
        });
    }
    return { ...orderSplitRates, errors, rates };
};

export const checkShippingRatesReady = ({ shippingRates }: IStore, shippingKey: string) =>
    shippingKey &&
    shippingKey === shippingRates.meta?.shippingKey && // rates are the same, address and line_items not changed
    !shippingRates.isFetching &&
    (shippingRates.payload?.ratesBySplit || shippingRates.payload?.error);

export const getShippingKey = (payload: IReviewTabStateAction["payload"], meta: IReviewTabStateAction["meta"]) =>
    md5(
        JSON.stringify([
            _.map(payload.order.line_items, (li) => _.pick(li, ["sizes", ...meta.orderSplitFields])),
            meta.address,
            payload.order.currency_code,
        ])
    );
